import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { Coins, RefreshCw, ExternalLink } from 'lucide-react'
import PageHeader from '@/components/ui/PageHeader'

/**
 * Prediction Markets — real-money crowd-sourced event probabilities.
 *
 * Surfaces the Polymarket + Metaculus/Kalshi odds that already flow through the
 * signal stream as a dedicated feed. Crowds putting actual capital behind
 * outcomes is a money-weighted forecast — often sharper than polls or pundits —
 * and a tradeable view on Fed decisions, macro prints, crypto, and geopolitics.
 */

type Market = {
  source: string
  ticker: string | null
  region: string | null
  implied_pct: number | null
  sentiment: number | null
  confidence: number | null
  volume_usd: number | null
  summary: string
  detail: string | null
  url: string | null
  created_at: string | null
}

/** Strip source-prefix noise (emoji, "Polymarket: NN% —") and the trailing
 *  "— NN% community forecast / Kalshi" so only the event question remains.
 *  Handles all three source summary formats. */
export function cleanMarketSummary(summary: string | null): string {
  let s = summary ?? ''
  s = s.replace(/^\s*[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}]?\s*/u, '')
  s = s.replace(/^Polymarket:\s*\d{1,3}%\s*[—-]\s*/i, '')
  s = s.replace(/^Polymarket:\s*/i, '')
  s = s.replace(/\s*[—-]\s*\d{1,3}%\s*(community forecast|Kalshi|YES)?\s*$/i, '')
  return s.trim()
}

function probColor(pct: number | null): string {
  if (pct == null) return 'text-text-muted'
  if (pct >= 75) return 'text-emerald-400'
  if (pct >= 55) return 'text-emerald-300'
  if (pct <= 25) return 'text-rose-400'
  if (pct <= 45) return 'text-rose-300'
  return 'text-amber-300'
}

function fmtUsd(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—'
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`
  return `$${n.toFixed(0)}`
}

const THEMES: { key: string; label: string; match: RegExp }[] = [
  { key: 'fed', label: 'Fed / Rates', match: /\bfed\b|fomc|rate cut|rate hike|interest rate|powell/i },
  { key: 'macro', label: 'Macro', match: /inflation|cpi|recession|gdp|unemployment|jobs|treasury|yield/i },
  { key: 'crypto', label: 'Crypto', match: /bitcoin|btc|ethereum|eth|crypto|solana|\bsol\b/i },
  { key: 'election', label: 'Elections', match: /election|president|senate|congress|vote|primary|poll/i },
  { key: 'geo', label: 'Geopolitics', match: /war|ceasefire|tariff|china|russia|ukraine|iran|israel|nato|opec|oil/i },
]

function themeOf(summary: string): string | null {
  const s = summary ?? ''
  for (const t of THEMES) {
    if (t.match.test(s)) return t.key
  }
  return null
}

export default function PredictionMarketsPage() {
  const [markets, setMarkets] = useState<Market[]>([])
  const [meta, setMeta] = useState<{ count?: number; with_ticker?: number; sources?: string[] }>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tickerOnly, setTickerOnly] = useState(false)
  const [theme, setTheme] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get('/api/signals/prediction-markets', { params: { limit: 80 } })
      setMarkets(data?.markets ?? [])
      setMeta({ count: data?.count, with_ticker: data?.with_ticker, sources: data?.sources ?? [] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load prediction markets')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const shown = markets.filter(m => {
    if (tickerOnly && !m.ticker) return false
    if (theme && themeOf(m.summary) !== theme) return false
    return true
  })

  // Theme counts so chips can show how many markets each covers.
  const themeCounts: Record<string, number> = {}
  for (const m of markets) {
    const t = themeOf(m.summary)
    if (t) themeCounts[t] = (themeCounts[t] ?? 0) + 1
  }

  return (
    <div className="animate-fade-in">
      <div className="max-w-screen-xl mx-auto">
        <PageHeader
          title="Prediction Markets"
          icon={Coins}
          iconClassName="text-amber-400"
          backTo={{ to: '/app', label: 'Command Centre' }}
          subtitle={<>
            Real-money implied probabilities from <span className="text-text-primary">Polymarket, Metaculus & Kalshi</span> —
            crowds putting capital behind outcomes, a money-weighted forecast that's often sharper than polls. A tradeable
            view on Fed decisions, macro prints, crypto and geopolitics.
          </>}
          actions={
            <button type="button" onClick={load} className="btn-ghost text-xs flex items-center gap-1">
              <RefreshCw size={13} /> Refresh
            </button>
          }
          className="mb-4"
        />

        <div className="glass rounded-xl px-3 py-2 flex items-center gap-2 mb-4 flex-wrap text-xs">
          {/* Theme filter chips */}
          <button type="button" onClick={() => setTheme(null)}
                  className={`px-3 py-1 rounded ${theme === null ? 'bg-amber-500/20 text-amber-300' : 'text-text-muted hover:text-text-primary'}`}>
            all
          </button>
          {THEMES.filter(t => (themeCounts[t.key] ?? 0) > 0).map(t => (
            <button key={t.key} type="button" onClick={() => setTheme(theme === t.key ? null : t.key)}
                    className={`px-3 py-1 rounded ${theme === t.key ? 'bg-amber-500/20 text-amber-300' : 'text-text-muted hover:text-text-primary'}`}>
              {t.label} <span className="opacity-60">{themeCounts[t.key]}</span>
            </button>
          ))}
          <span className="text-text-muted/40">|</span>
          <button type="button" onClick={() => setTickerOnly(v => !v)}
                  className={`px-3 py-1 rounded ${tickerOnly ? 'bg-blue-500/20 text-blue-300' : 'text-text-muted hover:text-text-primary'}`}>
            tradeable only
          </button>
          {meta.count != null && (
            <span className="ml-auto text-text-muted">
              {meta.count} markets · {meta.with_ticker ?? 0} mapped to a ticker
              {meta.sources && meta.sources.length > 0 ? ` · ${meta.sources.join(', ')}` : ''}
            </span>
          )}
        </div>

        {loading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="skeleton h-20" />
            ))}
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-400">
            {error} <button type="button" onClick={load} className="underline ml-2">Retry</button>
          </div>
        )}
        {!loading && !error && shown.length === 0 && (
          <div className="card p-6 text-center text-text-muted text-sm">
            No prediction-market signals yet. The Polymarket / Metaculus feeds populate on the next signal refresh.
          </div>
        )}

        <div className="space-y-2 stagger-children">
          {shown.map((m, i) => (
            <div key={`${m.summary?.slice(0, 40)}-${i}`} className="card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {m.ticker && (
                      <Link to={`/app/chart?symbol=${encodeURIComponent(m.ticker)}`}
                            className="font-mono font-bold text-blue-light hover:underline">{m.ticker}</Link>
                    )}
                    <span className="badge-neutral !text-[10px] uppercase tracking-wider">
                      {(m.source ?? '').replace(/_/g, ' ')}
                    </span>
                    {m.region && m.region !== 'Global' && (
                      <span className="text-[10px] text-text-muted">{m.region}</span>
                    )}
                  </div>
                  <p className="text-sm text-text-secondary mt-1.5">
                    {cleanMarketSummary(m.summary)}
                  </p>
                  {m.detail && <p className="text-[10px] text-text-muted mt-1">{m.detail}</p>}
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-2xl font-bold tabular-nums ${probColor(m.implied_pct)}`}>
                    {m.implied_pct != null ? `${m.implied_pct.toFixed(0)}%` : '—'}
                  </div>
                  <div className="text-[10px] text-text-muted">implied</div>
                  <div className="text-[10px] text-text-muted mt-1">vol {fmtUsd(m.volume_usd)}</div>
                </div>
              </div>
              {m.url && (
                <a href={m.url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-[10px] text-text-muted hover:text-blue-light mt-2">
                  <ExternalLink size={10} /> view market
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
